import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../../utils/AuthContext";
import { User as FirebaseUser } from "firebase/auth";
import { MagnifyingGlassIcon, UserIcon } from "@heroicons/react/24/outline";

const NavBar: React.FC = () => {
  const { isAdmin } = useAuth() as {
    isAdmin: boolean;
    currentUser: FirebaseUser | null;
    loading: boolean;
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState<string>(
    searchParams.get("query") || "",
  );

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleSearch = () => {
    const trimmed = searchTerm.trim();
    // Preserve other params and remove the 'query' key if empty
    const newParams = new URLSearchParams(searchParams);

    if (trimmed) newParams.set("query", trimmed);
    else newParams.delete("query");

    setSearchTerm(trimmed);

    setSearchParams(newParams);
    setIsMobileMenuOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <nav className="border-b py-8 z-[20] relative">
      <div className="flex items-center justify-between px-4 max-w-[100vw] mx-auto">
        {/* Left side: Logo + Primary navigation */}
        <div className="flex items-center space-x-8 w-full">
          <Link to="/" className="text-2xl font-bold font-Inter">
            The Motion Hub
          </Link>
          <nav className="hidden md:flex space-x-4">
            {isAdmin && (
              <>
                <Link
                  to="/admin"
                  className="text-sm text-gray-600 hover:text-gray-800 transition-colors cursor-pointer"
                >
                  Admin
                </Link>
                <Link
                  to="/admin/quick-add"
                  className="text-sm text-gray-600 hover:text-gray-800 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Quick Add
                </Link>
              </>
            )}
          </nav>
        </div>

        {/* Center: Search bar (desktop) */}
        <div className="hidden sm:flex items-center bg-gray-100 rounded-full px-3 py-2 max-w-[280px] w-1/3">
          <button type="button" onClick={handleSearch} aria-label="Search">
            <MagnifyingGlassIcon className="w-5 h-5 text-neutral-900" />
          </button>
          <input
            type="text"
            placeholder="Search bar"
            className="ml-2 w-full bg-transparent border-none outline-none"
            aria-label="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </div>

        {/* Right side: Secondary navigation (desktop) */}
        <div className="hidden md:flex items-center space-x-6 text-sm w-full justify-end">
          <Link
            to="/submit"
            className="text-gray-600 hover:text-gray-800 whitespace-nowrap"
          >
            Submit a video
          </Link>
          <Link
            to="/profile"
            className="text-gray-600 hover:text-gray-800 cursor-pointer"
          >
            <UserIcon className="w-6 h-6 font-neutral-950" />
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden flex items-center px-2 py-1"
          aria-label="Open Mobile Menu"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          <svg
            className="w-6 h-6 text-gray-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 bg-neutral-50 z-[2100] overflow-y-auto">
          <div className="p-8 text-center h-max flex justify-center flex-col items-center gap-16">
            <div className="flex justify-between items-center">
              <Link
                to="/"
                className="text-4xl font-bold"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                The Motion Hub
              </Link>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                aria-label="Close Mobile Menu"
                className="p-2 fixed top-8 right-4"
              >
                <svg
                  className="w-6 h-6 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <nav className="mt-4 space-y-4 text-center text-2xl flex flex-col gap-4">
              <Link
                to="/"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block text-gray-600 hover:text-gray-800"
              >
                Home
              </Link>

              {isAdmin && (
                <Link
                  to="/admin"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block text-gray-600 hover:text-gray-800"
                >
                  Admin
                </Link>
              )}
              <Link
                to="/boards"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block text-gray-600 hover:text-gray-800"
              >
                Favourites
              </Link>
              <Link
                to="/submit"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block text-gray-600 hover:text-gray-800"
              >
                Submit a video
              </Link>
              <Link
                to="/profile"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block text-gray-600 hover:text-gray-800"
              >
                Profile
              </Link>
            </nav>
            {/* Mobile Search */}
            <div className="mt-8 sm:hidden">
              <div className="mx-auto w-full max-w-[500px]">
                <div className="flex items-center bg-gray-100 rounded-full px-3 py-2">
                  <button type="button" onClick={handleSearch} aria-label="Search">
                    <MagnifyingGlassIcon className="w-5 h-5 text-neutral-900" />
                  </button>
                  <input
                    type="text"
                    placeholder="Search bar"
                    className="ml-2 w-full bg-transparent border-none outline-none text-center"
                    aria-label="Search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default NavBar;
